import { useQuery } from '@tanstack/react-query'
import { baseURL, headers, STALE_TIME } from '@/v2/constants/requests'

// Paginated daily performance data. Uses the backend's `daily_only=true`
// fast-path so paging through the Daily Performance table doesn't re-run the
// full (expensive) performance_metrics payload on every page change.
export const useDailyPerformance = (
  startDate,
  endDate,
  page = 1,
  { utmSource = '', utmMedium = '', utmCampaign = '' } = {}
) => {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      'dailyPerformance',
      startDate,
      endDate,
      page,
      utmSource,
      utmMedium,
      utmCampaign,
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
        daily_only: 'true',
        page,
        per_page: 10,
      })
      if (utmSource) params.append('utm_source', utmSource)
      if (utmMedium) params.append('utm_medium', utmMedium)
      if (utmCampaign) params.append('utm_campaign', utmCampaign)

      const res = await fetch(
        `${baseURL}/v2/rest/reports/performance_metrics?${params}`,
        {
          method: 'GET',
          headers,
        }
      )

      if (!res.ok) {
        if (res.status === 403) {
          throw new Error('Admin access required to view performance metrics')
        }
        throw new Error('Failed to fetch daily performance')
      }

      return res.json()
    },
    // Keep the current page visible while the next page loads.
    keepPreviousData: true,
    staleTime: STALE_TIME,
    cacheTime: STALE_TIME * 2,
    enabled: !!startDate && !!endDate,
    retry: 1,
  })

  return {
    dailyData: data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  }
}
